function addHubspotScript(d: Document, s: string, i: string)
{
  const j = d.createElement(s) as HTMLScriptElement;
  j.async = true;
  j.src = `//js.hs-scripts.com/${i}.js`;
  d.head.appendChild(j);
  return j;
}

// ---------------------------------------------------------------------------------------------------------------
// Showing the Hubspot cookie banner on local and the dev site
//
// Hubspot only shows the cookie banner for hostnames configured in the portal (the settings in its banner.js are
//  keyed by hostname) so it never shows on localhost or the dev site. On the hostnames listed below only, the
//  production banner settings are copied onto the current hostname before Hubspot loads so the banner shows as it
//  does in production. Every other hostname (including beta and production) is untouched.
// Like for any visitor the banner is hidden once a choice has been made, to see it again clear the cookies, use a
//  private window or run `_hsp.push(['revokeCookieConsent'])` in the console.
// ---------------------------------------------------------------------------------------------------------------
const BANNER_TEST_HOSTS = [
  'localhost',
  '127.0.0.1',
  'website-dev.schumacherhomes.com',
];
// The banner shown in production (portal and domain it is configured for). The portal the site runs against locally
//  has a different, simpler banner, so use the production one to see what visitors see.
const PROD_BANNER_PORTAL = '21376672';
const PROD_BANNER_DOMAIN = 'www.schumacherhomes.com';
const BANNER_SETTINGS_CALL = 'setBannerSettings';
const BANNER_STYLE_CALL = 'setCustomizationSettings';

type HubspotQueue = { _hsp?: unknown[][] };

function isBannerTestHost(): boolean
{
  return BANNER_TEST_HOSTS.includes(globalThis.location.hostname);
}

type BannerSettings = Record<string, unknown>;

// Reads one of the `_hsp.push(['<call>', {...}]);` settings calls at the top of the Hubspot banner script and
//  reuses the settings of the production domain for the current hostname
function readSettingsForCurrentHost(
  script: string,
  call: string,
): BannerSettings | null
{
  const callStart = script.indexOf(`_hsp.push(['${call}',`);
  if (callStart < 0) return null;

  const jsonStart = script.indexOf('{', callStart);
  const jsonEnd = script.indexOf(']);', jsonStart);
  const settings = JSON.parse(
    script.slice(jsonStart, jsonEnd),
  ) as BannerSettings;

  const hostname = globalThis.location.hostname;
  if (settings[PROD_BANNER_DOMAIN])
  {
    settings[hostname] = settings[PROD_BANNER_DOMAIN];
  }

  return settings;
}

// Hubspot's own script queues `setCustomizationSettings` (the banner styling) for the configured domain only and
//  each call replaces the previous one, which would wipe the styling set up for this hostname. Add the styling for
//  this hostname to that call as it is queued.
function keepStylingForCurrentHost(hsp: unknown[][], styles: BannerSettings)
{
  const hostname = globalThis.location.hostname;
  const queuePush = hsp.push;

  hsp.push = function (...entries: unknown[][])
  {
    for (const [call, settings] of entries)
    {
      if (
        call === BANNER_STYLE_CALL &&
        settings &&
        typeof settings === 'object'
      )
      {
        Object.assign(settings, { [hostname]: styles[hostname] });
      }
    }

    return queuePush.apply(hsp, entries);
  };
}

// Queues the banner settings for the current hostname ahead of the Hubspot script
async function seedBannerSettingsForCurrentHost()
{
  const queue = globalThis as typeof globalThis & HubspotQueue;
  const hsp = (queue._hsp = queue._hsp || []);

  try
  {
    const response = await fetch(
      `https://js.hs-banner.com/v2/${PROD_BANNER_PORTAL}/banner.js`,
    );
    const script = await response.text();

    const banner = readSettingsForCurrentHost(script, BANNER_SETTINGS_CALL);
    if (banner) hsp.push([BANNER_SETTINGS_CALL, banner]);

    const styles = readSettingsForCurrentHost(script, BANNER_STYLE_CALL);
    if (styles)
    {
      hsp.push([BANNER_STYLE_CALL, styles]);
      keepStylingForCurrentHost(hsp, styles);
    }
  }
  catch (error)
  {
    console.warn(
      'Unable to set up the Hubspot cookie banner for this host',
      error,
    );
  }
}

export function initHubspot(key: string)
{
  if (!key?.length) return;

  if (isBannerTestHost())
  {
    // The settings need to be queued before the Hubspot script runs
    void seedBannerSettingsForCurrentHost().then(() =>
      addHubspotScript(document, 'script', key),
    );
    return;
  }

  addHubspotScript(document, 'script', key);
}
